import {
    type FC,
    createContext,
    type ReactNode,
    useEffect,
    useTransition,
    useCallback,
} from 'react';
import { useRefetchableFragment, graphql } from 'react-relay';
import { getBuyerId } from 'dibs-cookie-jar';
import { type NftHeartPillProvider_viewer$key } from './__generated__/NftHeartPillProvider_viewer.graphql.ts';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers.ts';

type NftHeartPillContextValue = {
    itemLikeData: { nftId: string; isLiked: boolean; userLikeNodeId: string }[];
    isRefetching: boolean;
    refetch: () => void;
};

export const NftHeartPillContext = createContext<NftHeartPillContextValue | null>(null);

type Props = {
    viewer: NftHeartPillProvider_viewer$key | null | undefined;
    nftItemIds: string[];
    children: ReactNode;
};

export const NftHeartPillProvider: FC<Props> = ({ nftItemIds, viewer: viewerRef, children }) => {
    const [isRefetching, startTransition] = useTransition();
    const [viewer, refetchRelay] = useRefetchableFragment(
        graphql`
            fragment NftHeartPillProvider_viewer on Viewer
            @argumentDefinitions(
                userId: { type: "String", defaultValue: "" }
                nftIds: { type: "[String!]", defaultValue: "[]" }
                fetchUser: { type: "Boolean", defaultValue: false }
            )
            @refetchable(queryName: "NftHeartPillProviderRefetchQuery") {
                id
                isNftItemsLikedByUser(nftIds: $nftIds, userId: $userId) @include(if: $fetchUser) {
                    id
                    nftId
                    isLiked
                }
            }
        `,
        viewerRef
    );

    const refetch = useCallback(() => {
        const buyerId = getBuyerId(document.cookie);
        if (buyerId) {
            startTransition(() => {
                refetchRelay({ fetchUser: true, userId: buyerId, nftIds: nftItemIds || [] });
            });
        }
    }, [nftItemIds, refetchRelay, startTransition]);

    useEffect(() => {
        addLoginHandlers(() => {
            refetch();
        });
        refetch();
    }, [refetch]);

    const itemLikeData = [];
    for (const obj of viewer?.isNftItemsLikedByUser || []) {
        if (obj?.nftId && obj.id) {
            itemLikeData.push({
                userLikeNodeId: obj.id,
                nftId: obj.nftId,
                isLiked: !!obj.isLiked,
            });
        }
    }

    return (
        <NftHeartPillContext.Provider
            value={{
                isRefetching,
                itemLikeData,
                refetch,
            }}
        >
            {children}
        </NftHeartPillContext.Provider>
    );
};
